import { parseIntOrNull } from 'src/app/+shared/helpers/parsers.helper';
import {
  CcdDrrDevicesFormModel,
  ConsumptionDeviceDto as DrrCcdDeviceDto,
  DeviceFormModel,
  DeviceUnitDto,
  DeviceUnitFormModel,
  DeviceWrapperFormModel,
  DrrCcdDeviceApplicationDto,
  FileFormModel,
  SimpleDocumentDto,
} from './technical-data.domain';
import { toDateOnlyString } from 'src/app/+shared/helpers/date.helper';

export async function mapFormToDrrCcdDeviceApplicationDto(form): Promise<DrrCcdDeviceApplicationDto> {
  const drrCcdDeviceApplicationDto = new DrrCcdDeviceApplicationDto();
  drrCcdDeviceApplicationDto.is_registration_device = form.type === 'drr';
  drrCcdDeviceApplicationDto.is_consumption_device = form.type === 'ccd';
  drrCcdDeviceApplicationDto.network_connection_point_power = form.simultaneouslyRequiredPower;
  drrCcdDeviceApplicationDto.application_id = form.applicationId ?? 0;
  drrCcdDeviceApplicationDto.is_commissioning_done = form.is_commissioning_done;
  drrCcdDeviceApplicationDto.is_commissioning_confirmed = form.is_commissioning_confirmed;
  drrCcdDeviceApplicationDto.commissioning_date = toDateOnlyString(form.commissioning_date);
  drrCcdDeviceApplicationDto.commissioning_document = await mapFormToDocumentDto(form.drr_ccd_commissioning);
  drrCcdDeviceApplicationDto.consumption_devices = await Promise.all(
    form.devices.map(async d => await mapFormToDrrCcdDevice(d.device, form.applicationId))
  );

  return drrCcdDeviceApplicationDto;

  async function mapFormToDrrCcdDevice(device: DeviceFormModel, applicationId: any): Promise<DrrCcdDeviceDto> {
    const drrCcdDeviceDto = new DrrCcdDeviceDto();
    drrCcdDeviceDto.id = device.id;
    drrCcdDeviceDto.device_type = device.ccdType;
    drrCcdDeviceDto.pump_type = device.heatingPumpType;
    drrCcdDeviceDto.air_conditioning_type = device.airConditioningType;
    drrCcdDeviceDto.wall_box_type = device.wallBoxType;
    drrCcdDeviceDto.other_type = device.other;
    drrCcdDeviceDto.total_grid_power_consumption = device.totalConsumption;
    drrCcdDeviceDto.type_of_control = device.typeOfControl;
    drrCcdDeviceDto.module_choice = device.module;
    drrCcdDeviceDto.is_cascade_measurement = device.cascadeMeasurementDesired;
    drrCcdDeviceDto.is_separate_meter_required = device.separateMeterRequired;
    drrCcdDeviceDto.meter_number = device.meterNumber;
    drrCcdDeviceDto.meter2 = device.meter2;
    drrCcdDeviceDto.drr_ccd_device_application_id = applicationId ?? 0;

    drrCcdDeviceDto.device_units = await Promise.all(device.deviceUnits?.units?.map(async u => await mapFormToDeviceUnit(u, null)));

    return drrCcdDeviceDto;
  }

  async function mapFormToDeviceUnit(unitFormValue: DeviceUnitFormModel, parentId: any): Promise<DeviceUnitDto> {
    const deviceUnit = {} as DeviceUnitDto;
    deviceUnit.id = unitFormValue.id;
    deviceUnit.manufacturer = unitFormValue.manufacturer;
    deviceUnit.designation_type = unitFormValue.designationType;
    deviceUnit.power = unitFormValue.electricalPower;
    deviceUnit.heater_power = unitFormValue.powerOfAdditionalHeater;
    deviceUnit.has_additional_heating = unitFormValue.hasAdditionalHeating;
    deviceUnit.coupling_type = unitFormValue.couplingType;
    deviceUnit.has_load_management = unitFormValue.loadManagementAvailable;
    deviceUnit.load_management_type = unitFormValue.loadManagementType;
    deviceUnit.parent_device_id = parentId;
    deviceUnit.consumption_device_id = unitFormValue.consumptionDeviceId;
    deviceUnit.unavailable = unitFormValue.unavailable ?? false;
    deviceUnit.data_sheet_document = await mapFormToDocumentDto(unitFormValue.dataSheet);
    deviceUnit.data_sheet_wr_document = await mapFormToDocumentDto(unitFormValue.dataSheetWr);
    deviceUnit.circuit_diagram_document = await mapFormToDocumentDto(unitFormValue.circuitDiagramDocument);

    deviceUnit.children_devices = unitFormValue.subUnits
      ? await Promise.all(unitFormValue.subUnits.map(su => mapFormToDeviceUnit(su, unitFormValue.id)))
      : [];

    return deviceUnit;
  }
}

export function mapDrrCcdDeviceApplicationDtoToForm(dto: DrrCcdDeviceApplicationDto): CcdDrrDevicesFormModel {
  let formValue: CcdDrrDevicesFormModel = {
    applicationId: dto.application_id ? parseInt(dto.application_id.toString()) : null,
    type: dto.is_registration_device ? 'drr' : 'ccd',
    simultaneouslyRequiredPower: dto.network_connection_point_power,
    devices: dto.consumption_devices.map(mapConsumptionDeviceToForm),
    commissioningDate: dto.commissioning_date ? new Date(dto.commissioning_date) : null,
    isCommissioningDone: dto.is_commissioning_done,
    isCommissioningConfirmed: dto.is_commissioning_confirmed,
    commissioningDocumentId: dto.commissioning_document_id,
    commissioningDocument: mapDocumentDtoToForm(dto.commissioning_document),
  };

  return formValue;
}

function mapConsumptionDeviceToForm(dto: DrrCcdDeviceDto): DeviceWrapperFormModel {
  return {
    device: {
      id: parseIntOrNull(dto.id),
      ccdType: dto.device_type,
      heatingPumpType: dto.pump_type,
      airConditioningType: dto.air_conditioning_type,
      wallBoxType: dto.wall_box_type,
      other: dto.other_type,
      totalConsumption: dto.total_grid_power_consumption,
      typeOfControl: dto.type_of_control,
      module: dto.module_choice,
      cascadeMeasurementDesired: JSON.parse(dto.is_cascade_measurement?.toString()),
      separateMeterRequired: JSON.parse(dto.is_separate_meter_required?.toString()),
      meterNumber: dto.meter_number,
      meter2: dto.meter2,
      deviceUnits: {
        units: dto.device_units.map(mapDeviceUnitToForm),
      },
    },
  };
}
function mapDeviceUnitToForm(dto: DeviceUnitDto): DeviceUnitFormModel {
  return {
    id: parseIntOrNull(dto.id),
    manufacturer: dto.manufacturer,
    designationType: dto.designation_type,
    electricalPower: dto.power,
    powerOfAdditionalHeater: dto.heater_power,
    hasAdditionalHeating: JSON.parse(dto.has_additional_heating?.toString() ?? null),
    couplingType: dto.coupling_type,
    loadManagementAvailable: JSON.parse(dto.has_load_management?.toString() ?? null),
    loadManagementType: dto.load_management_type,
    consumptionDeviceId: dto.consumption_device_id,
    subUnits: dto.children_devices?.map(mapDeviceSubUnitToForm),
    unavailable: JSON.parse(dto.unavailable?.toString() ?? null),
    dataSheet: mapDocumentDtoToForm(dto.data_sheet_document),
    dataSheetWr: mapDocumentDtoToForm(dto.data_sheet_wr_document),
    circuitDiagramDocument: mapDocumentDtoToForm(dto.circuit_diagram_document),
  };
}

function mapDeviceSubUnitToForm(dto: DeviceUnitDto): DeviceUnitFormModel {
  return {
    manufacturer: dto.manufacturer,
    designationType: dto.designation_type,
    electricalPower: dto.power,
    powerOfAdditionalHeater: dto.heater_power,
    hasAdditionalHeating: JSON.parse(dto.has_additional_heating?.toString() ?? null),
    couplingType: dto.coupling_type,
    loadManagementType: dto.load_management_type,
    unavailable: JSON.parse(dto.unavailable?.toString() ?? null),
    id: parseIntOrNull(dto.id),
    consumptionDeviceId: dto.consumption_device_id,
    loadManagementAvailable: dto.has_load_management,
    dataSheet: mapDocumentDtoToForm(dto.data_sheet_document),
  };
}

function mapDocumentDtoToForm(dto: SimpleDocumentDto): FileFormModel {
  if (!dto) return null;

  return {
    id: dto.document_id,
    name: dto.name,
    file: null,
    contentType: dto.content_type,
  };
}

async function mapFormToDocumentDto(fileFormModel: FileFormModel): Promise<SimpleDocumentDto> {
  if (!fileFormModel || !fileFormModel.name) return null;

  return {
    document_id: fileFormModel?.id,
    name: fileFormModel?.name,
    document_type: fileFormModel.documentType,
    content_type: fileFormModel.contentType,
    content: await convertFileToBase64(fileFormModel?.file),
  };
}

function convertFileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve(null);
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file); // Reads file as Base64
    reader.onload = () => resolve(reader.result?.toString().split(',')[1] || ''); // Remove "data:*/*;base64,"
    reader.onerror = error => reject(error);
  });
}
